/**
 * Forms for single games of a running league (demo-ready PR 3):
 *   AddGameForm      POST  /leagues/:id/games
 *   EditGameForm     PATCH /leagues/:id/games/:gameId
 *   EnterResultForm  POST  /leagues/:id/games/:gameId/result
 * Each posts itself and calls onDone(message) on success; server errors
 * (403 / 409 / 400) are shown inside the form.
 */
import React, { useState } from 'react';
import { apiClient } from '../../../services/api/apiService';
import { usesSets } from '../leagueGames';

const MAX_SETS = 7;

const errorText = (error) => error?.response?.data?.message || error?.message || 'Something went wrong';

/** Court numbers with the host's names ("Boys Gym") where the league has them (PR 8). */
const courtOptions = (league) => {
  const names = Array.isArray(league?.game_rules?.courts) ? league.game_rules.courts : [];
  const count = Math.max(1, names.length || Number(league?.number_of_courts) || 1);
  return Array.from({ length: count }, (_, i) => ({ n: i + 1, label: names[i] || `Court ${i + 1}` }));
};

const entryKey = (team) => team.team_id ?? team.id;
const entryName = (team) => team.team_name || team.name || `Entry ${entryKey(team)}`;

const FormShell = ({ title, error, saving, submitLabel, onSubmit, onCancel, children }) => (
  <form
    className="league-game-form"
    aria-label={title}
    onClick={(event) => event.stopPropagation()}
    onSubmit={(event) => {
      event.preventDefault();
      onSubmit();
    }}
  >
    <h5 className="league-game-form__title">{title}</h5>
    {children}
    {error && <p className="league-game-form__error" role="alert">{error}</p>}
    <div className="league-game-form__actions">
      <button type="submit" className="league-game-form__submit" disabled={saving}>
        {saving ? 'Saving…' : submitLabel}
      </button>
      <button type="button" className="league-game-form__cancel" onClick={onCancel} disabled={saving}>
        Cancel
      </button>
    </div>
  </form>
);

const useSubmit = (onDone) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (request, message) => {
    setSaving(true);
    setError(null);
    try {
      await request();
      onDone?.(message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, setError, submit };
};

export const AddGameForm = ({ league, teams = [], onDone, onCancel }) => {
  const [form, setForm] = useState({
    game_date: '',
    game_time: String(league?.start_time || '19:00').slice(0, 5),
    court_number: 1,
    team_1_id: '',
    team_2_id: '',
  });
  const { saving, error, setError, submit } = useSubmit(onDone);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = () => {
    if (!form.game_date || !form.team_1_id || !form.team_2_id) {
      setError('Pick a date and both teams');
      return;
    }
    if (form.team_1_id === form.team_2_id) {
      setError('Pick two different teams');
      return;
    }
    submit(() => apiClient.post(`/leagues/${league.id}/games`, {
      ...form,
      court_number: Number(form.court_number),
      team_1_id: Number(form.team_1_id),
      team_2_id: Number(form.team_2_id),
    }), 'Game added to the schedule.');
  };

  return (
    <FormShell title="Add a game" error={error} saving={saving} submitLabel="Add game" onSubmit={handleSubmit} onCancel={onCancel}>
      <div className="league-game-form__grid">
        <label>Date<input type="date" value={form.game_date} onChange={set('game_date')} /></label>
        <label>Time<input type="time" value={form.game_time} onChange={set('game_time')} /></label>
        <label>Court
          <select value={form.court_number} onChange={set('court_number')}>
            {courtOptions(league).map((c) => <option key={c.n} value={c.n}>{c.label}</option>)}
          </select>
        </label>
        {['team_1_id', 'team_2_id'].map((key, i) => (
          <label key={key}>{i === 0 ? 'Team 1' : 'Team 2'}
            <select value={form[key]} onChange={set(key)}>
              <option value="">Choose…</option>
              {teams.map((t) => <option key={entryKey(t)} value={entryKey(t)}>{entryName(t)}</option>)}
            </select>
          </label>
        ))}
      </div>
    </FormShell>
  );
};

export const EditGameForm = ({ league, game, onDone, onCancel }) => {
  const [form, setForm] = useState({
    game_date: String(game.game_date || '').slice(0, 10),
    game_time: String(game.game_time || '').slice(0, 5),
    court_number: game.court_number || 1,
  });
  const { saving, error, submit } = useSubmit(onDone);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <FormShell
      title="Move this game"
      error={error}
      saving={saving}
      submitLabel="Save"
      onCancel={onCancel}
      onSubmit={() => submit(
        () => apiClient.patch(`/leagues/${league.id}/games/${game.id}`, { ...form, court_number: Number(form.court_number) }),
        'Game updated.'
      )}
    >
      <div className="league-game-form__grid">
        <label>Date<input type="date" value={form.game_date} onChange={set('game_date')} /></label>
        <label>Time<input type="time" value={form.game_time} onChange={set('game_time')} /></label>
        <label>Court
          <select value={form.court_number} onChange={set('court_number')}>
            {courtOptions(league).map((c) => <option key={c.n} value={c.n}>{c.label}</option>)}
          </select>
        </label>
      </div>
    </FormShell>
  );
};

const gamesToWin = (league) => {
  let rules = league?.game_rules;
  if (typeof rules === 'string') {
    try {
      rules = JSON.parse(rules);
    } catch {
      rules = null;
    }
  }
  const n = Number(rules?.games_to_win ?? rules?.gamesToWin);
  return Number.isInteger(n) && n > 0 ? Math.min(n, MAX_SETS) : 1;
};

export const EnterResultForm = ({ league, game, team1Name, team2Name, onDone, onCancel }) => {
  const setSport = usesSets(league?.sport_type);
  const [sets, setSets] = useState(() => Array.from({ length: gamesToWin(league) }, () => ({ p1: '', p2: '' })));
  const [totals, setTotals] = useState({ team_1_score: '', team_2_score: '' });
  const { saving, error, setError, submit } = useSubmit(onDone);

  const setSetScore = (index, side) => (event) => {
    const value = event.target.value;
    setSets((prev) => prev.map((s, i) => (i === index ? { ...s, [side]: value } : s)));
  };

  const handleSubmit = () => {
    const toNumber = (v) => (v === '' ? NaN : Number(v));
    let body;
    if (setSport) {
      const parsed = sets.map((s) => ({ p1: toNumber(s.p1), p2: toNumber(s.p2) }));
      if (parsed.some((s) => !Number.isInteger(s.p1) || !Number.isInteger(s.p2) || s.p1 < 0 || s.p2 < 0)) {
        setError('Enter both scores of every set');
        return;
      }
      body = { sets: parsed };
    } else {
      body = { team_1_score: toNumber(totals.team_1_score), team_2_score: toNumber(totals.team_2_score) };
      if (![body.team_1_score, body.team_2_score].every((n) => Number.isInteger(n) && n >= 0)) {
        setError('Enter both scores');
        return;
      }
    }
    submit(() => apiClient.post(`/leagues/${league.id}/games/${game.id}/result`, body), 'Result recorded.');
  };

  return (
    <FormShell title="Enter result" error={error} saving={saving} submitLabel="Save result" onSubmit={handleSubmit} onCancel={onCancel}>
      {setSport ? (
        <div className="league-game-form__sets">
          <div className="league-game-form__set league-game-form__set--head" aria-hidden="true">
            <span />
            <span title={team1Name}>{team1Name}</span>
            <span title={team2Name}>{team2Name}</span>
          </div>
          {sets.map((s, i) => (
            <div key={i} className="league-game-form__set">
              <span>Set {i + 1}</span>
              <input type="number" min="0" inputMode="numeric" aria-label={`Set ${i + 1} ${team1Name}`} value={s.p1} onChange={setSetScore(i, 'p1')} />
              <input type="number" min="0" inputMode="numeric" aria-label={`Set ${i + 1} ${team2Name}`} value={s.p2} onChange={setSetScore(i, 'p2')} />
            </div>
          ))}
          <div className="league-game-form__set-actions">
            {sets.length < MAX_SETS && (
              <button type="button" onClick={() => setSets((prev) => [...prev, { p1: '', p2: '' }])}>+ Add set</button>
            )}
            {sets.length > 1 && (
              <button type="button" onClick={() => setSets((prev) => prev.slice(0, -1))}>− Remove set</button>
            )}
          </div>
        </div>
      ) : (
        <div className="league-game-form__grid">
          <label>{team1Name}
            <input type="number" min="0" inputMode="numeric" value={totals.team_1_score}
              onChange={(e) => setTotals((prev) => ({ ...prev, team_1_score: e.target.value }))} />
          </label>
          <label>{team2Name}
            <input type="number" min="0" inputMode="numeric" value={totals.team_2_score}
              onChange={(e) => setTotals((prev) => ({ ...prev, team_2_score: e.target.value }))} />
          </label>
        </div>
      )}
    </FormShell>
  );
};
